/**
 * StepPill Component
 *
 * Large animated pill showing the active step with processing/complete states.
 * Uses framer-motion for smooth animations.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type StepStatus = 'pending' | 'processing' | 'complete';

export interface StepPillProps {
  step: number;
  title: string;
  description: string;
  status: StepStatus;
  thinkingMessage?: string;
  isActive: boolean;
}

export function StepPill({
  step,
  title,
  description,
  status,
  thinkingMessage,
  isActive,
}: StepPillProps) {
  return (
    <AnimatePresence mode="wait">
      {isActive && (
        <motion.div
          key={`step-pill-${step}`}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className={cn(
            'relative overflow-hidden rounded-2xl border-2 p-6 shadow-lg',
            status === 'processing' && 'border-blue-400 bg-blue-50',
            status === 'complete' && 'border-green-400 bg-green-50',
            status === 'pending' && 'border-gray-300 bg-gray-50',
          )}
        >
          {/* Animated background gradient for processing state */}
          {status === 'processing' && (
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-blue-100/50 via-blue-200/50 to-blue-100/50"
              animate={{
                x: ['0%', '100%', '0%'],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear',
              }}
              style={{ width: '200%', marginLeft: '-50%' }}
            />
          )}

          <div className="relative flex items-start gap-4">
            {/* Step number badge */}
            <div
              className={cn(
                'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-lg font-bold',
                status === 'processing' && 'bg-blue-500 text-white',
                status === 'complete' && 'bg-green-500 text-white',
                status === 'pending' && 'bg-gray-400 text-white',
              )}
            >
              {status === 'complete' ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                >
                  <Check className="h-6 w-6" />
                </motion.div>
              ) : (
                step
              )}
            </div>

            {/* Step content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h3
                  className={cn(
                    'text-xl font-semibold',
                    status === 'processing' && 'text-blue-900',
                    status === 'complete' && 'text-green-900',
                    status === 'pending' && 'text-gray-700',
                  )}
                >
                  {title}
                </h3>

                {/* Processing spinner */}
                {status === 'processing' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  </motion.div>
                )}
              </div>

              <p
                className={cn(
                  'mt-1 text-sm',
                  status === 'processing' && 'text-blue-700',
                  status === 'complete' && 'text-green-700',
                  status === 'pending' && 'text-gray-500',
                )}
              >
                {description}
              </p>

              {/* Thinking message */}
              <AnimatePresence mode="wait">
                {status === 'processing' && thinkingMessage && (
                  <motion.div
                    key={thinkingMessage}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-3 overflow-hidden"
                  >
                    <div className="flex items-center gap-2 rounded-lg bg-white/70 px-3 py-2 text-sm text-blue-800">
                      <motion.span
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        {thinkingMessage}
                      </motion.span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Step Progress Bar Component
 * Shows all steps in a horizontal progress indicator
 */
export interface StepProgressBarProps {
  currentStep: number;
  totalSteps: number;
  stepStatuses: Record<number, StepStatus>;
  stepTitles: string[];
}

export function StepProgressBar({
  currentStep,
  totalSteps,
  stepStatuses,
  stepTitles,
}: StepProgressBarProps) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map(
        (step, index) => {
          const status = stepStatuses[step] || 'pending';
          const isLast = index === totalSteps - 1;

          return (
            <React.Fragment key={step}>
              {/* Step indicator */}
              <motion.div
                className="flex flex-col items-center"
                initial={false}
                animate={{
                  scale: step === currentStep ? 1.1 : 1,
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors duration-300',
                    status === 'complete' && 'bg-green-500 text-white',
                    status === 'processing' && 'bg-blue-500 text-white',
                    status === 'pending' && 'bg-gray-200 text-gray-500',
                  )}
                >
                  {status === 'complete' ? (
                    <Check className="h-4 w-4" />
                  ) : status === 'processing' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    step
                  )}
                </div>
                <span
                  className={cn(
                    'mt-1 text-xs whitespace-nowrap max-w-[80px] truncate',
                    step === currentStep
                      ? 'font-medium text-gray-900'
                      : 'text-gray-500',
                  )}
                  title={stepTitles[index]}
                >
                  {stepTitles[index]}
                </span>
              </motion.div>

              {/* Connector line */}
              {!isLast && (
                <div className="relative h-0.5 w-8 bg-gray-200">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-green-500"
                    initial={{ width: '0%' }}
                    animate={{
                      width: status === 'complete' ? '100%' : '0%',
                    }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        },
      )}
    </div>
  );
}
